import McmMatterportConfig from '~/tenants/mcm/performable/matterport/McmMatterportConfig';
import McmJob from '~/tenants/mcm/model/McmJob';
import { FIRST_VERSION_TIMESTAMP, ZodVersionedMetadata } from '~/lib/zod';
import { TenantJobLine } from '~/lib/model';
import { Big } from 'big.js';
import { DeliverableType } from '~common/model/Deliverable';
import { PersonaType } from '~/tenants/common/TenantPersona';
import { McmOrderContext } from '~/tenants/mcm/model/McmOrder';
import { mcm_is_crossroads } from '~/tenants/mcm/util';
import { MCM_MATTERPORT_TIERS, MCM_MATTERPORT_EXTERIOR_TIERS } from '~/tenants/mcm/data';

export default class McmMatterportJob extends McmJob<typeof McmMatterportConfig> {
  get performable() {
    return McmMatterportConfig;
  }

  get deliverable() {
    return DeliverableType.MATTERPORT;
  }

  get media() {
    return PersonaType.PROVIDER;
  }

  defaultValue(): ZodVersionedMetadata<(typeof McmMatterportConfig)['schema']> {
    return {
      version: FIRST_VERSION_TIMESTAMP,
    };
  }

  revenueLines(): TenantJobLine[] {
    const lines: TenantJobLine[] = [];

    lines.push({
      id: 'matterport',
      description: (this.metadata.exterior ? 'Interior + Exterior' : 'Interior Only') + ' Matterport',
      amount: McmMatterportJob.calculate(this.order, this.metadata.exterior),
    });

    if (this.metadata.floorplan) {
      lines.push({
        id: 'floorplan',
        description: 'Floorplan',
        amount: new Big(75),
      });
    }

    return lines;
  }

  static calculate(order: McmOrderContext, exterior = false) {
    const sqft = order.metadata.sqft || 2000;

    // Choose the appropriate tier set based on exterior option
    const tiers = exterior ? MCM_MATTERPORT_EXTERIOR_TIERS : MCM_MATTERPORT_TIERS;
    const tier = tiers.find(t => sqft <= t.max);
    let price: Big;
    
    if (tier) {
      price = tier.price;
    } else {
      // Beyond max tier, use formula
      const basePrice = exterior ? new Big('799.95') : new Big('699.95');
      const additionalSqft = Math.max(0, sqft - 12000);
      const additionalCharges = new Big(Math.ceil(additionalSqft / 500)).times(new Big('25'));
      price = basePrice.plus(additionalCharges);
    }

    // Crossroads packages override tier pricing
    if (mcm_is_crossroads(order, 'platinum')) {
      price = new Big('300');
    } else if (mcm_is_crossroads(order, 'gold')) {
      price = new Big('250');
    } else if (mcm_is_crossroads(order, 'silver')) {
      price = new Big('200');
    } else if (mcm_is_crossroads(order, 'basic')) {
      price = new Big('150');
    }

    return price;
  }

  get eligible(): boolean {
    return !this.buyer('crossroads') || this.orderHasPerformable('zillow');
  }

  onsite(): number {
    const additionalSqft = Math.max(0, this.order.metadata.sqft - 2000);

    return 60 + Math.ceil(additionalSqft / 1000) * 15;
  }

  get configurable() {
    return !this.buyer('crossroads');
  }
}
